/**
 * Read-only renderer for chapter content (TipTap JSON) with anti-copy guards.
 *
 * Guards applied:
 *  - CSS user-select: none + draggable images disabled
 *  - onCopy / onCut / onContextMenu / onDragStart event blockers
 *  - Keyboard interception of Ctrl/Cmd + C / X / S / P
 *  - @media print { visibility: hidden } (via class on parent)
 *  - Transparent overlay on top to capture pointer events for text selection
 *
 * A determined developer with DevTools can still extract the JSON content;
 * this is an industry-realistic deterrent, not a vault.
 */
import { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";

const swallow = (e) => {
  e.preventDefault();
  e.stopPropagation();
  return false;
};

const onKey = (e) => {
  const meta = e.ctrlKey || e.metaKey;
  if (!meta) return;
  const k = (e.key || "").toLowerCase();
  if (["c", "x", "s", "p", "u"].includes(k)) {
    e.preventDefault();
    e.stopPropagation();
  }
};

export default function ChapterReader({ value }) {
  const wrapperRef = useRef(null);

  const editor = useEditor({
    editable: false,
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Image.configure({ inline: false, allowBase64: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || { type: "doc", content: [] },
    editorProps: {
      attributes: {
        class: "tiptap-reader prose prose-slate max-w-none focus:outline-none",
      },
      handleDOMEvents: {
        copy: (_v, e) => {
          e.preventDefault();
          return true;
        },
        cut: (_v, e) => {
          e.preventDefault();
          return true;
        },
        dragstart: (_v, e) => {
          e.preventDefault();
          return true;
        },
      },
    },
  });

  // Sync content when switching chapters
  useEffect(() => {
    if (!editor) return;
    editor.commands.setContent(value || { type: "doc", content: [] }, false);
  }, [value, editor]);

  // Capture keyboard at the wrapper level
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    el.addEventListener("keydown", onKey, true);
    return () => el.removeEventListener("keydown", onKey, true);
  }, []);

  return (
    <div
      ref={wrapperRef}
      data-testid="chapter-reader"
      tabIndex={0}
      onCopy={swallow}
      onCut={swallow}
      onContextMenu={swallow}
      onDragStart={swallow}
      className="chapter-reader relative select-none"
      style={{
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
        userSelect: "none",
      }}
    >
      <EditorContent editor={editor} />
      {/* Print-blocking style (one global rule is enough but inline-scoped here) */}
      <style>{`
        @media print {
          .chapter-reader { visibility: hidden !important; }
        }
        .chapter-reader img { -webkit-user-drag: none; user-drag: none; pointer-events: none; }
      `}</style>
    </div>
  );
}
